import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarRange, CheckCircle2, XCircle } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { notificar } from '@/shared/ui/avisos';
import { CampoDelPanel, PanelLateral, claseEntradaPanel } from '@/shared/ui/PanelLateral';
import type { MotivoDeCorreccion } from '@/shared/api/tipos';
import { cajasQueAcreditan } from './cajas';

interface Punto {
  id: number;
  codigo: string;
  nombre: string;
  activo: boolean;
  usuarioNombre: string | null;
  comprobantes?: { tipo: string; habilitado: boolean }[];
  deNotas?: boolean;
}

interface MesCorregido {
  periodo: string;
  que: string;
  facturaNuevaId: number | null;
  notaDeCredito: string | null;
  detalle: string;
}

export function DialogoDeCorreccionEnLote({
  abierto,
  clienteId,
  clienteNombre,
  meses,
  alCerrar,
  alTerminar,
}: {
  abierto: boolean;
  clienteId: number | null;
  clienteNombre: string;

  meses: string[];
  alCerrar: () => void;
  alTerminar: () => void;
}) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [motivoId, setMotivoId] = useState('');
  const [observacion, setObservacion] = useState('');
  const [aprobadoPor, setAprobadoPor] = useState('');
  const [punto, setPunto] = useState('');
  const [reemitir, setReemitir] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<MesCorregido[] | null>(null);

  const motivos = useQuery({
    queryKey: ['motivos-de-correccion'],
    queryFn: () => api.get<MotivoDeCorreccion[]>('/motivos-de-correccion'),
    enabled: abierto,
  });

  const puntos = useQuery({
    queryKey: ['puntos-emision'],
    queryFn: () => api.get<Punto[]>('/puntos-emision'),
    enabled: abierto,
  });

  const cajas = cajasQueAcreditan(puntos.data ?? []);
  const elegido = (motivos.data ?? []).find((m) => String(m.id) === motivoId) ?? null;

  const ordenados = [...meses].sort();

  const falta =
    desde === ''
      ? 'Elija el primer mes'
      : hasta === ''
        ? 'Elija el último mes'
        : desde > hasta
          ? 'El primer mes es posterior al último'
          : motivoId === ''
            ? 'Elija el motivo'
            : elegido?.exigeObservacion && observacion.trim() === ''
              ? 'Este motivo necesita que se anote el detalle'
              : elegido?.requiereAprobacion && aprobadoPor.trim() === ''
                ? 'Este motivo necesita la aprobación de un responsable'
                : null;

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (clienteId === null || falta !== null) {
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      const r = await api.post<{ meses: MesCorregido[] }>(
        `/clientes/${clienteId}/correcciones/en-lote?puntoEmision=${punto || 0}`,
        {
          desde,
          hasta,
          motivoId: Number(motivoId),
          observacion: observacion.trim() || null,
          aprobadoPor: aprobadoPor.trim() || null,
          respaldo: null,
          reemitir,
        },
      );

      setResultado(r.meses);
      const corregidos = r.meses.filter((m) => m.facturaNuevaId !== null).length;
      notificar.exito(
        corregidos === 0
          ? 'No se corrigió ningún mes: revise el detalle'
          : `${corregidos} ${corregidos === 1 ? 'mes corregido' : 'meses corregidos'}`,
      );
      alTerminar();
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo corregir el tramo';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  function cerrar() {
    setResultado(null);
    setError(null);
    alCerrar();
  }

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={cerrar}
      titulo="Corregir varios meses"
      descripcion={clienteNombre}
      icono={CalendarRange}
      {...(resultado === null ? { alEnviar: enviar, enviando } : {})}
    >
      {resultado !== null ? (
        <div className="space-y-3">
          <p className="text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
            Esto es lo que se hizo con cada mes. Lo que no se pudo tocar queda como estaba y se dice
            por qué.
          </p>

          {resultado.map((m) => (
            <FilaDelMes key={m.periodo} mes={m} />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-amber-800">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            <span>
              Cada mes se corrige con la tarifa y la exoneración de hoy, no con las de entonces. Los
              meses que el SRI ya autorizó se acreditan con nota de crédito, una por mes.
            </span>
          </p>

          <div className="grid grid-cols-2 gap-3">
            <CampoDelPanel etiqueta="Desde" requerido>
              <select
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                className={claseEntradaPanel}
              >
                <option value="">MES</option>
                {ordenados.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </CampoDelPanel>

            <CampoDelPanel etiqueta="Hasta" requerido>
              <select
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                className={claseEntradaPanel}
              >
                <option value="">MES</option>
                {ordenados
                  .filter((p) => desde === '' || p >= desde)
                  .map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
              </select>
            </CampoDelPanel>
          </div>

          <CampoDelPanel etiqueta="Motivo" requerido>
            <select
              value={motivoId}
              onChange={(e) => setMotivoId(e.target.value)}
              className={claseEntradaPanel}
            >
              <option value="">SELECCIONAR</option>
              {(motivos.data ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.codigo} · {m.nombre}
                </option>
              ))}
            </select>
          </CampoDelPanel>

          <CampoDelPanel
            etiqueta="Detalle"
            requerido={elegido?.exigeObservacion ?? false}
            ayuda="El mismo para todo el tramo: es el mismo error repetido mes a mes"
          >
            <textarea
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              rows={3}
              className={`${claseEntradaPanel} h-auto py-2`}
            />
          </CampoDelPanel>

          {elegido?.requiereAprobacion && (
            <CampoDelPanel etiqueta="Autorizó" requerido>
              <input
                value={aprobadoPor}
                onChange={(e) => setAprobadoPor(e.target.value)}
                className={claseEntradaPanel}
              />
            </CampoDelPanel>
          )}

          <CampoDelPanel
            etiqueta="Punto de emisión"
            ayuda="Solo hace falta si algún mes necesita nota de crédito"
          >
            <select
              value={punto}
              onChange={(e) => setPunto(e.target.value)}
              className={claseEntradaPanel}
            >
              <option value="">NINGUNO</option>
              {cajas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.codigo} · {c.usuarioNombre}
                </option>
              ))}
            </select>
          </CampoDelPanel>

          <label className="flex items-start gap-2 text-[11px] uppercase text-armazon-700">
            <input
              type="checkbox"
              checked={reemitir}
              onChange={(e) => setReemitir(e.target.checked)}
              className="mt-0.5 size-4 rounded border-armazon-300"
            />
            <span>
              Emitir también la factura corregida de cada mes.
              <span className="block text-armazon-500">
                Sin esto, los meses quedan anulados y esperando su corregida.
              </span>
            </span>
          </label>

          {(error || falta) && (
            <p
              role={error ? 'alert' : undefined}
              className={`rounded-lg border p-3 text-[11px] uppercase ${
                error
                  ? 'border-red-200 bg-red-50 text-red-900'
                  : 'border-armazon-200 bg-armazon-50 text-armazon-600'
              }`}
            >
              {error ?? falta}
            </p>
          )}
        </div>
      )}
    </PanelLateral>
  );
}

function FilaDelMes({ mes: m }: { mes: MesCorregido }) {
  const bien = m.facturaNuevaId !== null;
  const mal = m.que === 'NO_SE_PUEDE' || m.que === 'FALLO';

  return (
    <div
      className={`rounded-lg border p-3 ${
        bien
          ? 'border-marca-200 bg-marca-50/50'
          : mal
            ? 'border-amber-200 bg-amber-50/60'
            : 'border-armazon-200 bg-white'
      }`}
    >
      <p className="flex items-center gap-2">
        {bien ? (
          <CheckCircle2 className="size-3.5 shrink-0 text-marca-600" aria-hidden />
        ) : mal ? (
          <XCircle className="size-3.5 shrink-0 text-amber-600" aria-hidden />
        ) : (
          <AlertTriangle className="size-3.5 shrink-0 text-armazon-400" aria-hidden />
        )}
        <span className="importe text-xs font-bold text-armazon-800">{m.periodo}</span>
        {m.notaDeCredito && (
          <span className="importe rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
            NC {m.notaDeCredito}
          </span>
        )}
      </p>
      <p className="mt-1 text-justify text-[11px] uppercase leading-relaxed text-armazon-600">
        {m.detalle}
      </p>
    </div>
  );
}
